import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * 🔍 Vollbild-Foto mit Zoom (Wunsch Green Goat: „full screen photo and pinch with fingers").
 * - Zwei Finger: zoomen (1×–6×), ein Finger bei Zoom: verschieben
 * - Doppeltipp: 2,5× auf die Stelle / zurück auf 1×
 * - Mausrad (PC): zoomen
 * - Schließen: ✕ oben rechts, oder einfacher Tipp bei 1×
 * Per Portal an <body> — sonst schluckt der Karten-Swipe der Paketliste die Gesten.
 */
export default function ZoomImageViewer({ src, onClose }) {
  const [view, setView] = useState({ s: 1, x: 0, y: 0 });
  const viewRef = useRef(view);
  viewRef.current = view;
  const gesture = useRef(null);
  const lastTap = useRef(0);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const clamp = (s) => Math.min(6, Math.max(1, s));
  const setClamped = (v) => setView(v.s <= 1.001 ? { s: 1, x: 0, y: 0 } : v);

  const dist = (t) => Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY);
  const mid = (t) => ({ x: (t[0].clientX + t[1].clientX) / 2, y: (t[0].clientY + t[1].clientY) / 2 });

  const onTouchStart = (e) => {
    e.stopPropagation();
    const t = e.touches;
    const v = viewRef.current;
    if (t.length === 2) {
      gesture.current = { type: 'pinch', d0: dist(t), m0: mid(t), v0: v };
    } else if (t.length === 1) {
      gesture.current = { type: 'pan', p0: { x: t[0].clientX, y: t[0].clientY }, v0: v, moved: false, t0: Date.now() };
    }
  };

  const onTouchMove = (e) => {
    e.stopPropagation();
    const g = gesture.current;
    if (!g) return;
    const t = e.touches;
    if (g.type === 'pinch' && t.length === 2) {
      const s = clamp(g.v0.s * (dist(t) / g.d0));
      const m = mid(t);
      // Zoom um den Fingermittelpunkt + Mitverschieben mit den Fingern
      const cx = window.innerWidth / 2;
      const cy = window.innerHeight / 2;
      const k = s / g.v0.s;
      const x = (g.m0.x - cx) - k * (g.m0.x - cx - g.v0.x) + (m.x - g.m0.x);
      const y = (g.m0.y - cy) - k * (g.m0.y - cy - g.v0.y) + (m.y - g.m0.y);
      setClamped({ s, x, y });
    } else if (g.type === 'pan' && t.length === 1) {
      const dx = t[0].clientX - g.p0.x;
      const dy = t[0].clientY - g.p0.y;
      if (Math.hypot(dx, dy) > 8) g.moved = true;
      if (g.v0.s > 1) setView({ s: g.v0.s, x: g.v0.x + dx, y: g.v0.y + dy });
    }
  };

  const onTouchEnd = (e) => {
    e.stopPropagation();
    const g = gesture.current;
    if (e.touches.length > 0) {
      // ein Finger bleibt liegen → nahtlos als Verschieben weiter
      const t = e.touches[0];
      gesture.current = { type: 'pan', p0: { x: t.clientX, y: t.clientY }, v0: viewRef.current, moved: true, t0: Date.now() };
      return;
    }
    gesture.current = null;
    if (!g || g.type !== 'pan' || g.moved || Date.now() - g.t0 > 350) return;
    // Tipp: Doppeltipp = Zoom umschalten, Einzeltipp bei 1× = schließen
    const now = Date.now();
    const pt = e.changedTouches[0];
    if (now - lastTap.current < 320) {
      lastTap.current = 0;
      const v = viewRef.current;
      if (v.s > 1) setView({ s: 1, x: 0, y: 0 });
      else {
        const s = 2.5;
        const cx = window.innerWidth / 2;
        const cy = window.innerHeight / 2;
        setView({ s, x: -(pt.clientX - cx) * (s - 1), y: -(pt.clientY - cy) * (s - 1) });
      }
      e.preventDefault();
      return;
    }
    lastTap.current = now;
    setTimeout(() => {
      if (lastTap.current === now && viewRef.current.s <= 1) onClose();
    }, 330);
  };

  const onWheel = (e) => {
    e.stopPropagation();
    const v = viewRef.current;
    const s = clamp(v.s * (e.deltaY < 0 ? 1.15 : 1 / 1.15));
    const cx = window.innerWidth / 2;
    const cy = window.innerHeight / 2;
    const k = s / v.s;
    setClamped({ s, x: (e.clientX - cx) - k * (e.clientX - cx - v.x), y: (e.clientY - cy) - k * (e.clientY - cy - v.y) });
  };

  const onClick = (e) => {
    // PC-Klick (kein Touch): bei 1× schließen
    e.stopPropagation();
    if (e.detail && viewRef.current.s <= 1 && !('ontouchstart' in window)) onClose();
  };

  return createPortal(
    <div
      onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd}
      onWheel={onWheel} onClick={onClick}
      style={{
        position: 'fixed', inset: 0, zIndex: 9000, background: 'rgba(0,0,0,0.95)',
        overflow: 'hidden', touchAction: 'none', userSelect: 'none',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      <img src={src} alt="" draggable={false}
        style={{
          maxWidth: '100%', maxHeight: '100%', objectFit: 'contain',
          transform: `translate(${view.x}px, ${view.y}px) scale(${view.s})`,
          transformOrigin: 'center center', willChange: 'transform',
          transition: gesture.current ? 'none' : 'transform 0.15s ease-out',
        }} />
      <button type="button" aria-label="Close"
        onTouchStart={(e) => { e.stopPropagation(); }}
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        onTouchEnd={(e) => { e.stopPropagation(); e.preventDefault(); onClose(); }}
        style={{
          position: 'absolute',
          top: 'max(48px, calc(env(safe-area-inset-top, 36px) + 12px))',
          right: 16,
          zIndex: 2,
          width: 48,
          height: 48,
          borderRadius: '50%',
          border: '1.5px solid rgba(255,255,255,0.35)',
          background: 'rgba(20,20,20,0.75)',
          color: '#fff',
          fontSize: 22,
          fontWeight: 700,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          boxShadow: '0 2px 10px rgba(0,0,0,0.5)',
        }}>✕</button>
    </div>,
    document.body,
  );
}
